<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'UserGuide',
  },
})

const fieldTable = [
  { field: 'Dari Tanggal', wajib: true, penjelasan: 'Tanggal awal periode laporan', contoh: '01/07/2026' },
  { field: 'Sampai Tanggal', wajib: true, penjelasan: 'Tanggal akhir periode laporan', contoh: '05/07/2026' },
  { field: 'Kelompokkan', wajib: true, penjelasan: 'Cara data dikelompokkan di tabel: Harian, Mingguan, atau Bulanan', contoh: 'Harian' },
]

const faq = [
  {
    q: 'Apa bedanya "Kelompokkan: Harian" dan "Mingguan"?',
    a: 'Harian menampilkan satu baris untuk setiap tanggal. Mingguan menggabungkan data 7 hari jadi satu baris. Bulanan menggabungkan satu bulan jadi satu baris. Gunakan Harian untuk analisa detail, dan Bulanan untuk melihat tren jangka panjang.',
  },
  {
    q: 'Kenapa saya klik "Tampilkan" tapi datanya tidak berubah?',
    a: 'Pastikan "Dari Tanggal" dan "Sampai Tanggal" sudah benar-benar terisi. Jika "Sampai Tanggal" lebih awal dari "Dari Tanggal", sistem tidak akan menampilkan data. Perbaiki urutan tanggalnya lalu klik Tampilkan lagi.',
  },
  {
    q: 'File Excel/PDF yang saya download isinya sesuai filter yang saya pilih?',
    a: 'Ya. Tombol Excel dan PDF akan mengekspor data sesuai dengan Dari Tanggal, Sampai Tanggal, dan Kelompokkan yang sedang aktif saat itu di layar.',
  },
  {
    q: 'Apa itu "Gross Margin" di kartu ringkasan?',
    a: 'Gross Margin adalah persentase Laba Kotor dibanding Revenue. Contoh: Revenue Rp 10.000.000, Laba Kotor Rp 4.000.000, maka Gross Margin = 40%. Semakin tinggi persennya, semakin efisien toko Anda mengelola biaya bahan baku.',
  },
  {
    q: '"Top 10 Produk" dihitung berdasarkan apa?',
    a: 'Berdasarkan total pendapatan (revenue) tertinggi dari produk tersebut selama periode yang Anda pilih, bukan berdasarkan jumlah unit terjual semata.',
  },
]
</script>

<template>
  <div>
    <!-- Breadcrumb -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center gap-2 mb-2">
          <RouterLink to="/userguide" class="text-decoration-none text-medium-emphasis text-body-2">
            User Guide
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <RouterLink to="/userguide/laporan" class="text-decoration-none text-medium-emphasis text-body-2">
            Laporan
          </RouterLink>
          <VIcon icon="ri-arrow-right-s-line" size="16" class="text-medium-emphasis" />
          <span class="text-body-2">Laporan Penjualan</span>
        </div>
      </VCol>
    </VRow>

    <!-- Header -->
    <VRow>
      <VCol cols="12">
        <div class="d-flex align-center mb-4">
          <VAvatar color="success" variant="tonal" size="56" rounded="lg" class="me-3">
            <VIcon icon="ri-line-chart-line" size="30" />
          </VAvatar>
          <div>
            <h4 class="text-h4 font-weight-bold mb-0">Laporan Penjualan</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Analisa penjualan berdasarkan periode
            </p>
          </div>
        </div>

        <VAlert type="info" variant="tonal" icon="ri-flag-line">
          <strong>Tujuan halaman ini:</strong> Membantu Anda melihat performa
          penjualan pada rentang tanggal tertentu — lengkap dengan produk
          terlaris dan metode pembayaran yang paling sering dipakai pelanggan.
        </VAlert>
      </VCol>
    </VRow>

    <!-- Konsep -->
    <VRow class="mt-6">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-book-open-line" class="me-2" color="success" />
            Apa itu Laporan Penjualan?
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              Kalau Dashboard hanya menunjukkan angka <strong>hari ini</strong>,
              Laporan Penjualan bisa menunjukkan angka untuk <strong>rentang
              waktu berapa pun</strong> yang Anda mau — misalnya "1 sampai 31 Juli",
              atau "minggu lalu saja".
            </p>
            <p class="text-body-2 mb-0">
              Laporan ini juga bisa dikelompokkan per hari, per minggu, atau
              per bulan — mirip seperti melihat rekening koran, tapi khusus
              untuk penjualan toko Anda.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Cara Penggunaan -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-cursor-line" class="me-2" color="success" />
            Cara Menggunakan
          </VCardTitle>
          <VCardText>
            <ol class="text-body-2 ps-4">
              <li class="mb-3">
                Buka menu <strong>Laporan → Penjualan</strong> dari sidebar kiri
              </li>
              <li class="mb-3">
                Isi <strong>"Dari Tanggal"</strong> dan <strong>"Sampai Tanggal"</strong>
                sesuai periode yang ingin dilihat (contoh: 01/07/2026 s.d. 05/07/2026)
              </li>
              <li class="mb-3">
                Pilih <strong>"Kelompokkan"</strong>: Harian, Mingguan, atau Bulanan
              </li>
              <li class="mb-3">
                Klik tombol <strong>"Tampilkan"</strong>
              </li>
              <li class="mb-0">
                Kalau ingin menyimpan laporan, klik tombol <strong>"Excel"</strong>
                atau <strong>"PDF"</strong> di sebelah kanan
              </li>
            </ol>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Tabel Field -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-file-list-3-line" class="me-2" color="info" />
            Penjelasan Filter
          </VCardTitle>
          <VCardText>
            <VTable>
              <thead>
                <tr>
                  <th>Field</th>
                  <th style="min-width: 80px">Wajib?</th>
                  <th>Penjelasan</th>
                  <th>Contoh</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in fieldTable" :key="row.field">
                  <td><strong>{{ row.field }}</strong></td>
                  <td>
                    <VChip :color="row.wajib ? 'error' : 'default'" size="x-small" variant="tonal">
                      {{ row.wajib ? '✅ Ya' : '❌ Tidak' }}
                    </VChip>
                  </td>
                  <td class="text-body-2 py-2">{{ row.penjelasan }}</td>
                  <td class="text-body-2 py-2">{{ row.contoh }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Penjelasan Hasil -->
    <VRow class="mt-4">
      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-numbers-line" class="me-2" color="primary" />
            Kartu Ringkasan
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-2">
              Enam kartu di bagian atas menunjukkan total selama periode yang dipilih:
            </p>
            <ul class="text-body-2 ps-4 mb-0">
              <li class="mb-1"><strong>Total Order</strong> — jumlah transaksi</li>
              <li class="mb-1"><strong>Revenue</strong> — total omzet</li>
              <li class="mb-1"><strong>Diskon</strong> — total potongan harga yang diberikan</li>
              <li class="mb-1"><strong>HPP</strong> — total biaya bahan baku</li>
              <li class="mb-1"><strong>Laba Kotor</strong> — Revenue dikurangi Diskon dan HPP</li>
              <li class="mb-0"><strong>Gross Margin</strong> — persentase Laba Kotor dibanding Revenue</li>
            </ul>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="6">
        <VCard class="h-100">
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-table-line" class="me-2" color="primary" />
            Tabel "Detail per Periode"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-2">
              Tabel ini merinci angka per baris waktu sesuai pilihan "Kelompokkan".
              Contoh untuk Harian:
            </p>
            <VTable density="compact">
              <thead>
                <tr>
                  <th>Periode</th>
                  <th>Order</th>
                  <th>Revenue</th>
                  <th>Laba Kotor</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>03 Jul 2026</td>
                  <td>18</td>
                  <td>Rp 1.250.000</td>
                  <td class="text-info">Rp 520.000</td>
                </tr>
                <tr>
                  <td>04 Jul 2026</td>
                  <td>22</td>
                  <td>Rp 1.480.000</td>
                  <td class="text-info">Rp 610.000</td>
                </tr>
              </tbody>
            </VTable>
            <p class="text-caption text-medium-emphasis mt-2 mb-0">
              Baris paling bawah tabel menampilkan total keseluruhan periode.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Top Produk & Payment -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VCard>
          <VCardTitle class="pa-4 pb-2">
            <VIcon icon="ri-medal-line" class="me-2" color="warning" />
            "Top 10 Produk" dan "Metode Pembayaran"
          </VCardTitle>
          <VCardText>
            <p class="text-body-2 mb-3">
              <strong>Top 10 Produk</strong> menampilkan 10 produk dengan
              pendapatan (revenue) tertinggi selama periode yang dipilih,
              lengkap dengan jumlah unit terjual.
            </p>
            <p class="text-body-2 mb-0">
              <strong>Metode Pembayaran</strong> menunjukkan total transaksi
              dan nilai uang untuk setiap cara bayar (tunai/cash, kartu, dll).
              Berguna untuk tahu metode mana yang paling sering dipakai pelanggan Anda.
            </p>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Catatan Penting -->
    <VRow class="mt-4">
      <VCol cols="12">
        <VAlert type="warning" variant="tonal" icon="ri-alert-line">
          <strong>Catatan Penting:</strong> Jika "Sampai Tanggal" Anda isi lebih
          awal dari "Dari Tanggal", laporan tidak akan menampilkan data.
          Pastikan urutan tanggal sudah benar sebelum klik Tampilkan.
        </VAlert>
      </VCol>
    </VRow>

    <!-- FAQ -->
    <VRow class="mt-4">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">❓ Pertanyaan yang Sering Ditanyakan</h5>
        <VExpansionPanels variant="accordion">
          <VExpansionPanel v-for="(item, i) in faq" :key="i">
            <VExpansionPanelTitle class="text-body-2 font-weight-medium">
              {{ item.q }}
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <p class="text-body-2 mb-0">{{ item.a }}</p>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </VCol>
    </VRow>

    <!-- Related -->
    <VRow class="mt-6">
      <VCol cols="12">
        <h5 class="text-h5 font-weight-bold mb-3">🔗 Halaman Terkait</h5>
        <VRow>
          <VCol
            v-for="link in [
              { title: 'Dashboard', sub: 'Ringkasan cepat hari ini', icon: 'ri-dashboard-line', color: 'primary', to: '/userguide/laporan/dashboard' },
              { title: 'Laporan Laba Rugi', sub: 'Lihat untung/rugi bersih', icon: 'ri-file-chart-line', color: 'warning', to: '/userguide/laporan/laba-rugi' },
              { title: 'Laporan Inventory', sub: 'Cek status stok bahan baku', icon: 'ri-archive-line', color: 'info', to: '/userguide/laporan/inventory' },
            ]"
            :key="link.title"
            cols="12"
            md="4"
          >
            <RouterLink :to="link.to" class="text-decoration-none">
              <VCard variant="outlined" hover>
                <VCardText class="d-flex align-center gap-3 py-3">
                  <VAvatar :color="link.color" variant="tonal" size="40" rounded>
                    <VIcon :icon="link.icon" size="22" />
                  </VAvatar>
                  <div>
                    <p class="text-body-2 font-weight-bold mb-0">{{ link.title }}</p>
                    <p class="text-caption text-medium-emphasis mb-0">{{ link.sub }}</p>
                  </div>
                </VCardText>
              </VCard>
            </RouterLink>
          </VCol>
        </VRow>
      </VCol>
    </VRow>
  </div>
</template>
